import { Button } from '../components/Button.js';
import { Icon } from '../components/Icon.js';
import { RETENTION } from '../fixtures/config.js';
import type { ClosingVariant } from '../fixtures/domain.js';
import { deletionUrgency, type Deletion, type DeletionUrgency } from '../fixtures/rules.js';
import { formatCountdownLong, formatDeadline } from '../util/format.js';
import { useDismiss } from '../util/useDismiss.js';
import styles from './AutoEndDialog.module.css';

/**
 * CSS Modules are typed as a string index, so `styles[urgency]` is unchecked in
 * both directions — renaming a DeletionUrgency member would silently yield
 * undefined and drop the styling. This map turns that into a compile error.
 */
const TONE: Record<DeletionUrgency, string> = {
  normal: styles.normal!,
  warning: styles.warning!,
  urgent: styles.urgent!,
};

interface AutoEndDialogProps {
  deletion: Deletion;
  now: number;
  counterpartName: string;
  ended: boolean;
  variant: ClosingVariant;
  onClose: () => void;
  onEnd: () => void;
}

const CONFIRM: Record<ClosingVariant, string> = {
  reject: 'End & reject now',
  close: 'End now',
};

/**
 * What the countdown pill means, and the shortcut to acting on it now instead
 * of waiting the clock out. The headline carries the countdown because that is
 * the thing being explained; the body carries the date and what happens next.
 */
export function AutoEndDialog({
  deletion,
  now,
  counterpartName,
  ended,
  variant,
  onClose,
  onEnd,
}: AutoEndDialogProps) {
  useDismiss(true, onClose);

  // One word for one fate. Both clocks end in a hard delete, so "ends" would be
  // inventing a state the product does not have.
  const title = `Deletes in ${formatCountdownLong(deletion.at, now)}`;

  return (
    <div className={styles.overlay} onMouseDown={onClose}>
      <div
        className={styles.dialog}
        role="dialog"
        aria-modal="true"
        onMouseDown={(event) => event.stopPropagation()}
      >
        <h2 className={`${styles.title} ${TONE[deletionUrgency(deletion, now)]}`}>
          <Icon name={ended ? 'trash' : 'clockFading'} size={18} />
          {title}
        </h2>
        <p className={styles.description}>{body({ deletion, ended, counterpartName, now })}</p>

        <div className={styles.footer}>
          <Button variant="ghost" onClick={onClose}>
            Close
          </Button>
          {!ended && (
            <Button variant="danger" onClick={onEnd}>
              {CONFIRM[variant]}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

function body({
  deletion,
  ended,
  counterpartName,
  now,
}: Pick<AutoEndDialogProps, 'deletion' | 'ended' | 'counterpartName' | 'now'>): string {
  const when = formatDeadline(deletion.at, now);
  const minutes = RETENTION.endChatWindowMs / 60_000;

  // The title already carries the countdown, so the body only owes the reader
  // the two things it cannot know: the exact date, and whether it can act.
  //
  // Open chats lead with the reset, because that is the news — the clock is an
  // idle timer, not a verdict. "Any" rather than "reply": the sweep keys off the
  // last message from either side, and the employer has often sent last, so
  // there is nothing to reply to.
  if (!ended) {
    return `Any new message resets the ${RETENTION.inactivityDays}-day timer. Without one, this chat and its full history are deleted ${when}.`;
  }

  // Ended chats can't be reset, so there is no action to lead with — just say
  // what happens. A read is the only thing that pins a short, certain deadline.
  if (deletion.reason === 'read') {
    return `${counterpartName} read your last message. This chat and its full history are deleted ${when}.`;
  }

  return `Deleted ${when}, or ${minutes} minutes after ${counterpartName} reads your last message.`;
}
